import React, { Fragment, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';
import { Alert, LayoutSidebar, SectionHeader } from 'components';
import { toRem } from 'design';
import { MPContent } from 'screens/MakePayment/components';
import { selectSiteSettings } from 'store/Site';
import { setPOSResults, updateCalculatorPaymentset } from 'store/TipCalculator/actions';
import { selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults } from 'store/TipCalculator/selectors';
import { DEFAULT_POOL_ID, DEFAULT_POOL_UNKNOWN_JOB_TITLE, ICalculatorRule, ICalculatorRules, IEmployerSite, IPOSSegmentTotals, ITipCalculatorChangeLogEntry, SourcePaymentType, SourceType, TicketOverrideType } from 'types';
import { capitalize, toTitleCase } from 'utils/strings';
import { buildSortedChecksList, buildTipDataChangeLogEntries, calculateCreditCardFee, getSharedPOSJobTitles, getSortedUniqueSegments } from 'utils/tipCalculator';
import { CalculateProcessingModal } from '../components/modals/CalculateProcessingModal';
import AccordionListItem from '../components/TipData/AccordionListItem/AccordionListItem';
import { IEmployeeListItemProps } from '../components/TipData/EmployeeListItem/EmployeeListItem';
import Summary, { ITipTotals } from '../components/TipData/Summary/Summary';

dayjs.extend(advancedFormat);

interface ITipDataProps {
  currentCalculatorRules: ICalculatorRules;
  currentSite: IEmployerSite;
  onBack: () => void;
  onExpand: () => void;
  pageHeaderContent: React.ReactNode;
  sidebarOpen?: boolean;
}

const ListItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  :not(:last-child) {
    margin-bottom: ${toRem(20)};
  }
`;

const TipData = ({ currentCalculatorRules, currentSite, onBack, onExpand, pageHeaderContent, sidebarOpen }: ITipDataProps) => {
  const dispatch = useDispatch();
  const { holdback: currentSiteHoldbacks, tipCalculatorPercentFee: currentSiteTipCalcPercentFee } = useSelector(selectSiteSettings()) || {};

  // POS Data
  const posResults = useSelector(selectCalculatorPOSResults());

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const [currentChangeLogEntries, setCurrentChangeLogEntries] = useState<ITipCalculatorChangeLogEntry[]>(changeLogEntries);

  // Payment Set State
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentDate = paymentSet?.date;
  const formattedDate = dayjs(paymentDate).format('MMMM Do, YYYY');

  // get the calculator rules where there is a share policy and build array of jobs with a share policy
  const rulesWithSharePolicy: ICalculatorRule[] = currentCalculatorRules.rules.filter((rule) => rule.sharePolicy);

  /// Build jobs array for share policy rules
  const sharePolicyJobs: { title: string; default: boolean }[] = [];
  rulesWithSharePolicy.forEach((policy) => {
    if (policy.contributor) sharePolicyJobs.push({ title: policy.contributor.name.toLowerCase(), default: policy.default });
    policy.additionalContributors?.forEach((additionalJob) => sharePolicyJobs.push({ title: additionalJob.name.toLowerCase(), default: policy.default })); // add all additional jobs
    if (policy.includeNoLabor === true) sharePolicyJobs.push({ title: DEFAULT_POOL_UNKNOWN_JOB_TITLE, default: policy.default }); // add `unknown` job to handle includeNoLabor
  });

  /**
   * this clone is used throughout the component to store any
   * changes made and will be compared to the original to determine
   * if the user has made any changes
   */
  const [paymentSetClone, setPaymentSetClone] = useState(structuredClone(paymentSet));
  const [integrationDataClone, setIntegrationDataClone] = useState(structuredClone(posResults));
  const segments = useMemo(() => {
    const uniqueSegments = getSortedUniqueSegments(integrationDataClone, paymentSetClone.segments);
    return uniqueSegments.map((segment) => {
      const segmentDetails = paymentSetClone.segments.find((s) => s.id === segment.id);
      return { ...segment, ...segmentDetails };
    });
  }, [integrationDataClone, paymentSetClone]);

  const dirty = !isEqual(paymentSet, paymentSetClone) || !isEqual(posResults, integrationDataClone);

  const calculateUpdatedTotals = (currentTotals: IPOSSegmentTotals, source: SourceType, type: SourcePaymentType, value: number, creditCardPercentFee: number): IPOSSegmentTotals => {
    const updatedTotals = { ...currentTotals };
    const sourceTotals = { ...currentTotals[source] };

    // Update the specific payment type value
    sourceTotals[type] = value;

    // Calculate credit card fee if needed
    if (type === SourcePaymentType.CREDIT) {
      sourceTotals.creditCardFee = calculateCreditCardFee(value, creditCardPercentFee);
      sourceTotals.gross = currentTotals[source].cash + value;
      sourceTotals.net = currentTotals[source].cash + value - sourceTotals.creditCardFee;
    } else {
      sourceTotals.gross = currentTotals[source].creditCard + value;
      sourceTotals.net = currentTotals[source].creditCard + value - currentTotals[source].creditCardFee;
    }

    // Update the source totals
    updatedTotals[source] = sourceTotals;

    // Calculate overall gross and net
    updatedTotals.gross = updatedTotals.tip.gross + updatedTotals.autoGratuity.gross;
    updatedTotals.net = updatedTotals.tip.net + updatedTotals.autoGratuity.net;

    return updatedTotals;
  };

  const onTotalsChange = (id: string, jobTitle: string, segmentId: string, source: SourceType, type: SourcePaymentType, value: number, creditCardPercentFee: number) => {
    const payee = integrationDataClone.data.find((data) => (data.employee ? data.employee?.id === id : data.provider.employeeId === id));

    if (payee) {
      const segment = payee.segments.find((seg) => seg.id === segmentId && seg.job.title.trim().toLowerCase() === jobTitle.trim().toLowerCase());

      if (segment) {
        // build change log entries
        const changeLogEntries = buildTipDataChangeLogEntries(
          {
            id: payee.provider.employeeId,
            name: payee.employee?.name || `${payee.provider.firstName} ${payee.provider.lastName}`,
            jobTitle: jobTitle.trim().toLowerCase(),
            segmentName: segment.name,
            source,
            type,
            value,
            totals: segment.totals,
          },
          currentChangeLogEntries
        );

        // Calculate updated totals
        const updatedSegmentTotals = calculateUpdatedTotals(segment.totals, source, type, value, creditCardPercentFee);
        segment.totals = updatedSegmentTotals;

        // Calculate payee totals by summing all segment totals
        const updatedPayeeTotals = payee.segments.reduce(
          (acc, seg) => {
            acc.tip.cash += seg.totals.tip.cash;
            acc.tip.creditCard += seg.totals.tip.creditCard;
            acc.tip.creditCardFee += seg.totals.tip.creditCardFee;
            acc.tip.gross += seg.totals.tip.gross;
            acc.tip.net += seg.totals.tip.net;

            acc.autoGratuity.cash += seg.totals.autoGratuity.cash;
            acc.autoGratuity.creditCard += seg.totals.autoGratuity.creditCard;
            acc.autoGratuity.creditCardFee += seg.totals.autoGratuity.creditCardFee;
            acc.autoGratuity.gross += seg.totals.autoGratuity.gross;
            acc.autoGratuity.net += seg.totals.autoGratuity.net;

            return acc;
          },
          {
            tip: { cash: 0, creditCard: 0, creditCardFee: 0, gross: 0, net: 0 },
            autoGratuity: { cash: 0, creditCard: 0, creditCardFee: 0, gross: 0, net: 0 },
          }
        );

        payee.totals = {
          ...payee.totals,
          tip: updatedPayeeTotals.tip,
          autoGratuity: updatedPayeeTotals.autoGratuity,
          gross: updatedPayeeTotals.tip.gross + updatedPayeeTotals.autoGratuity.gross,
          net: updatedPayeeTotals.tip.net + updatedPayeeTotals.autoGratuity.net,
        };

        // update change log entries
        setCurrentChangeLogEntries(changeLogEntries);

        // update clone
        setIntegrationDataClone(integrationDataClone);
        setPaymentSetClone({ ...paymentSetClone });
      }
    }
  };

  const renderListItemForShareSegments = useMemo(
    () =>
      segments.map((segment, idx) => {
        // initialize array of segment entry items
        const segmentEntryItems: IEmployeeListItemProps[] = [];

        // populate segment entry items
        integrationDataClone.data.forEach((data) => {
          const matchSegments = data.segments.filter((seg) => seg.id === segment.id);
          matchSegments.forEach((match) => {
            // Only include employees who have worked jobs with a share policy
            // In the case of the default pool, only include employees who are contrubuting
            if (sharePolicyJobs.find((item) => item.title.toLowerCase() === match.job.title.toLowerCase() && item.default === (match.id === DEFAULT_POOL_ID))) {
              const overrideTickets = match.tickets.filter((ticket) => !!ticket.override);

              segmentEntryItems.push({
                title: data?.employee?.name || `${data?.provider?.firstName ?? ''} ${data?.provider?.lastName ?? ''}`.trim() || capitalize(DEFAULT_POOL_UNKNOWN_JOB_TITLE),
                subText: toTitleCase(match.job.title),
                totals: match.totals,
                onTotalsChange: (source, type, value) => onTotalsChange(data.employee ? data.employee.id : data.provider.employeeId, match.job.title, segment.id, source, type, value, currentSiteTipCalcPercentFee),
                sharedPOS: !data.employee && getSharedPOSJobTitles(currentCalculatorRules).includes(data.provider.schedules[0]?.jobTitle.toLowerCase()),
                creditCardFeeEnabled: !!currentSiteTipCalcPercentFee,
                creditCardPercentFee: currentSiteTipCalcPercentFee,
                overrideTickets,
              });
            }
          });
        });

        if (!segmentEntryItems.length) return <Fragment key={`segment-${segment.id}`} />; // Do not build entry if we don't have segment entries.

        // calculate total
        const total = segmentEntryItems.reduce((acc, entryItem) => acc + entryItem.totals.net, 0);

        return (
          <ListItemWrapper key={`segment-${segment.id}`} data-cy="tip-data-list-item">
            <AccordionListItem key={`segment-${segment.id}`} title={segment.name} defaultOpenState={idx === 0 && true} totalAmount={total} listItems={segmentEntryItems} />
          </ListItemWrapper>
        );
      }),
    [segments, integrationDataClone]
  );

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <CalculateProcessingModal
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={paymentDate}
        siteId={currentSite.id}
        siteHoldbackSettings={currentSiteHoldbacks}
        segments={paymentSetClone.segments}
        posDataResults={integrationDataClone}
        creditCardPercentFee={currentSiteTipCalcPercentFee}
        changeLogEntries={currentChangeLogEntries}
        onSuccess={() => {
          // update original with confirmed new data
          dispatch(updateCalculatorPaymentset(paymentSetClone));
          dispatch(setPOSResults(integrationDataClone));
        }}
      />
    ),
    [currentCalculatorRules, paymentDate, currentSite, currentSiteHoldbacks, paymentSetClone, integrationDataClone]
  );

  const [showAbandonChangesModal, hideAbandonChangesModal] = useModal(
    () => (
      <Alert
        data-cy="abandon-changes-alert"
        title="Abandon Changes?"
        message="You have unsaved changes that haven't been applied to your calculations. Are you sure you want to discard edits?"
        onLeftAction={hideAbandonChangesModal}
        onRightAction={onBack}
        leftActionLabel="Cancel"
        rightActionLabel="Abandon Changes"
        rightActionAppearance="destructive"
        isActive
      />
    ),
    [onBack]
  );

  const onNext = () => {
    showIntegrationDataProcessingModal();
  };

  const calculateSummaryTotals = useMemo((): ITipTotals => {
    // calculate the totals for share
    let cashTips = 0;
    let cashAutograts = 0;
    let ccTips = 0;
    let ccAutograts = 0;
    let ccFeeSum = 0;

    integrationDataClone?.data?.forEach((entry) => {
      entry.segments.forEach((seg) => {
        cashTips += seg.totals.tip.cash;
        cashAutograts += seg.totals.autoGratuity.cash;
        ccTips += seg.totals.tip.creditCard;
        ccAutograts += seg.totals.autoGratuity.creditCard;
        ccFeeSum += seg.totals.tip.creditCardFee + seg.totals.autoGratuity.creditCardFee;
      });
    });

    // Get tip total of split checks
    const splitChecks = buildSortedChecksList(integrationDataClone).filter((check) => check.override?.type === TicketOverrideType.SPLIT);
    const splitChecksTotal = splitChecks.reduce((acc, check) => acc + check.totals.tip.net + check.totals.autoGratuity.net, 0); // Split check total
    ccFeeSum += splitChecks.reduce((acc, check) => acc + check.totals.tip.creditCardFee + check.totals.autoGratuity.creditCardFee, 0); // Split check credit card fee

    // Get tip total of ignored checks
    const ignoredChecks = buildSortedChecksList(integrationDataClone).filter((check) => check.override?.type === TicketOverrideType.IGNORE);
    const ignoredChecksTotal = ignoredChecks.reduce((acc, check) => acc + check.totals.tip.net + check.totals.autoGratuity.net, 0); // Ignore check total
    ccFeeSum += ignoredChecks.reduce((acc, check) => acc + check.totals.tip.creditCardFee + check.totals.autoGratuity.creditCardFee, 0); // Ignore check credit card fee

    return { cashTips, cashAutograts, ccTips, ccAutograts, ccFeeSum, splitChecksTotal, ignoredChecksTotal };
  }, [integrationDataClone, paymentSetClone]);

  const allowedToProceed = useMemo(() => {
    // Allow $0 values for line items (tip-share employees or tip-pool segments), but collected total must be greater than 0
    const { cashTips, cashAutograts, ccTips, ccAutograts } = calculateSummaryTotals;
    return (cashTips || 0) + (cashAutograts || 0) + (ccTips || 0) + (ccAutograts || 0) > 0;
  }, [calculateSummaryTotals]);

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}
          <MPContent data-cy="tip-data-list">
            <SectionHeader data-cy="tip-data-header" title="Collected Tips" />
            {renderListItemForShareSegments}
          </MPContent>
        </>
      }
      sidebar={
        <Summary
          allowedToProceed={allowedToProceed && dirty}
          totals={calculateSummaryTotals}
          onBackClick={dirty ? showAbandonChangesModal : onBack}
          onExpand={onExpand}
          onNextClick={onNext}
          rightActionLabel="Recalculate"
          sidebarOpen={sidebarOpen}
          creditCardPercentFee={currentSiteTipCalcPercentFee}
          creditCardFeeEnabled={!!currentSiteTipCalcPercentFee}
          formattedDate={formattedDate}
        />
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default TipData;
